import { HTMLAttributes } from 'react';
import { MenuItem, Select } from '@mui/material';
import styles from './Table.module.scss';
import PureTable from '../../../common/PureTable/PureTable';
import { PureSeparator } from '../../compound/Separator/Separator';
import Pagination from '../Pagination/Pagination';
import { SortArrows } from '../../../../lib/icons/SortArrows';
import { TABLE_LABELS } from '../../../../lib/constants/labelKeys';
import { Label } from '../../../common/Label/Label';
import { classes } from '../../../../lib/utils/cssUtils';
import { LeftArrow } from '../../../../lib/icons/LeftArrow';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import { useId } from '../../../../lib/hooks/useId';
import { tableIds } from '../../../../lib/tests/testIds';
import { TableProps } from './Table.types';
import FilterSearchBox from '../FilterPanel/FilterSearchBox/FilterSearchBox';
import Dropdown, { DropdownItem } from '../../../common/Dropdown/Dropdown';
import RefreshLoader from '../../../common/RefreshLoader/RefreshLoader';
import { TableTitleRowSkeleton } from './TableSkeletons';
import HeadingWithIcon from '../../../common/HeadingWithIcon/HeadingWithIcon';
import { useDirectionLabel, SortValue } from '../../../../lib/hooks/labels';
import { CY_SEARCH_AND_SORT } from '../../../../lib/constants/cypress';

const WrappedArrowIcon = ({ className, ...rest }: HTMLAttributes<HTMLOrSVGElement>) => (
  <SortArrows {...rest} className={classes(className, styles.sortIconContainer)} />
);

const Table = ({
  className,
  heading,
  customClasses,
  headingLevel,
  headingDisplayLevel,
  showPageSizeSelector,
  totalPages,
  activePage,
  handlePageChange,
  headings,
  handleItemsOnPage,
  pageSizeOptions,
  activeSortValue,
  sortValues,
  handleSortChange,
  pageSize,
  data,
  dataOrder,
  search,
  mobileVariant,
  selected,
  icon,
  isInitialLoad,
  isLoading,
  emptyPlaceholdersCount,
  children,
  disableBlurring,
  desktopCenteredColumns,
}: TableProps) => {
  const headingId = useId(`table-heading-`);
  const getDirectionLabel = useDirectionLabel();

  const sortValuesWithDirection = (sortValues as SortValue[])?.map(({ value, labelKey }) => ({
    value,
    labelKey,
    labelKeyDirection: getDirectionLabel({ value }),
  }));

  return (
    <div className={classes(styles.container, customClasses?.wrapper)}>
      {!!children && (
        <div className={styles.refreshLoaderContainer}>
          {isLoading && <RefreshLoader />}
          {children}
        </div>
      )}
      <div className={classes(styles.tableContainer, className)}>
        <div className={classes(styles.headingContainer, customClasses?.headContainer)}>
          {isInitialLoad ? (
            <TableTitleRowSkeleton withTitleOrSearch={!!heading || !!search} />
          ) : (
            <>
              {!!heading && (
                <HeadingWithIcon
                  id={headingId}
                  as={headingDisplayLevel}
                  level={headingLevel ?? HeadingLevel.h3}
                  icon={icon}
                  classNames={{ icon: customClasses?.headingIcon, heading: customClasses?.heading }}
                >
                  {heading}
                </HeadingWithIcon>
              )}
              {!!search && (
                <FilterSearchBox
                  className={styles.searchBox}
                  value={search.phrase ?? ''}
                  onChange={search.handleChange}
                  customPlaceholderKey={search.placeholderKey}
                  isLoading={isLoading || isInitialLoad}
                  disableBlurring={disableBlurring}
                />
              )}
              {!!headings?.length && sortValues?.length && (
                <div className={styles.sortingContainer}>
                  <Label name={TABLE_LABELS.SORT_BY} className={styles.sortByTitle} />
                  <Dropdown
                    value={activeSortValue}
                    onChange={handleSortChange}
                    IconComponent={WrappedArrowIcon}
                    data-testid={tableIds.sortSelector}
                    data-cy={CY_SEARCH_AND_SORT.SORT_SELECTOR}
                  >
                    {sortValuesWithDirection?.map(({ value, labelKey, labelKeyDirection }) => (
                      <DropdownItem key={value} value={value}>
                        <Label name={labelKey} />
                        &nbsp;
                        <Label name={labelKeyDirection} />
                      </DropdownItem>
                    ))}
                  </Dropdown>
                </div>
              )}
            </>
          )}
        </div>
        <div className={classes(styles.refreshLoaderContainer, customClasses?.refreshLoaderContainer)}>
          {isLoading && <RefreshLoader />}
          <PureTable
            aria-labelledby={headingId}
            emptyPlaceholdersCount={emptyPlaceholdersCount}
            selected={selected}
            data={data}
            dataOrder={dataOrder}
            mobileVariant={mobileVariant}
            headings={headings}
            isInitialLoad={isInitialLoad}
            desktopCenteredColumns={desktopCenteredColumns}
          />
          <PureSeparator className={styles.separator} />
          <div className={styles.bottomSummaryRow}>
            {showPageSizeSelector && !!headings?.length && (
              <div className={styles.pageSizeContainer}>
                <Label name={TABLE_LABELS.ITEMS_PER_PAGE_PREFIX} />
                <Select
                  value={pageSize}
                  onChange={event => handleItemsOnPage(event.target.value as number)}
                  IconComponent={LeftArrow}
                  classes={{
                    select: styles.selectedPageSize,
                    icon: styles.pageSizeSelectorIcon,
                    iconOpen: styles.pageSizeSelectorIconOpened,
                  }}
                  data-testid={tableIds.itemsPerPageSelector}
                >
                  {pageSizeOptions.map(entry => (
                    <MenuItem key={entry} value={entry} className={styles.pageSizeEntry}>
                      {entry}
                    </MenuItem>
                  ))}
                </Select>
                <Label name={TABLE_LABELS.ITEMS_PER_PAGE_SUFFIX} />
              </div>
            )}
            <Pagination
              className={styles.pagination}
              currentPage={activePage}
              totalPages={totalPages}
              onChange={handlePageChange}
              isLoading={isInitialLoad}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Table;
